import { BuildingCode } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

const Asce7_10 = Symbol('ASCE 7-10');
const Asce7_16 = Symbol('ASCE 7-16');
const Asce7_22 = Symbol('ASCE 7-22');

const asce7VersionIndices = {
  [Asce7_10]: 0,
  [Asce7_16]: 1,
  [Asce7_22]: 2,
};
export type Asce7Version = keyof typeof asce7VersionIndices;

const nonBreakingSpace = '\u00A0';
const asce7String = `ASCE${nonBreakingSpace}7`;

export function formatAsce7Version(asce7Version: Asce7Version | null) {
  switch (asce7Version) {
    case null:
      return asce7String;
    case Asce7_10:
      return `${asce7String}-10`;
    case Asce7_16:
      return `${asce7String}-16`;
    case Asce7_22:
      return `${asce7String}-22`;
  }
}

export function asceSection(...sections: string[]) {
  return asceSectionWithVersion(null, ...sections);
}
export function asceSectionWithVersion(asce7Version: Asce7Version | null, ...sections: string[]) {
  return `${formatAsce7Version(asce7Version)} Section${sections.length > 1 ? 's' : ''} ${sections.join(' & ')}`;
}

function isAsce7VersionAtLeast(version1: Asce7Version, version2: Asce7Version) {
  return asce7VersionIndices[version1] >= asce7VersionIndices[version2];
}

export function isAsce7_16OrLater(asce7Version: Asce7Version) {
  return isAsce7VersionAtLeast(asce7Version, Asce7_16);
}
export function isAsce7_22OrLater(asce7Version: Asce7Version) {
  return isAsce7VersionAtLeast(asce7Version, Asce7_22);
}

// This should mirror `StructuralLoading.LoadSettingsExtensions.GetAsce7Version`.
export function getAsce7Version(buildingCode: BuildingCode) {
  switch (buildingCode) {
    case BuildingCode.Bcnys2025:
    case BuildingCode.Cbc2025:
    case BuildingCode.Crc2025:
    case BuildingCode.Fbc2023:
    case BuildingCode.Fbc2026:
    case BuildingCode.Frc2023:
    case BuildingCode.Frc2026:
    case BuildingCode.Ibc2024:
    case BuildingCode.Irc2024:
    case BuildingCode.Rcnys2025:
      return Asce7_22;

    case BuildingCode.Bcnys2020:
    case BuildingCode.Cbc2019:
    case BuildingCode.Cbc2022:
    case BuildingCode.Crc2022:
    case BuildingCode.Fbc2020:
    case BuildingCode.Frc2020:
    case BuildingCode.Ibc2018:
    case BuildingCode.Ibc2021:
    case BuildingCode.Irc2018:
    case BuildingCode.Irc2021:
    case BuildingCode.Scbc2018:
      return Asce7_16;

    case BuildingCode.Fbc2010:
    case BuildingCode.Fbc2013:
    case BuildingCode.Fbc2017:
    case BuildingCode.Frc2010:
    case BuildingCode.Frc2013:
    case BuildingCode.Frc2017:
    case BuildingCode.Ibc2012:
    case BuildingCode.Ibc2015:
    case BuildingCode.Irc2012:
    case BuildingCode.Irc2015:
    case BuildingCode.MnsrcIrc2012:
    case BuildingCode.Ncbc2018:
    case BuildingCode.WiscIbc2015:
      return Asce7_10;

    case BuildingCode.Unknown:
    case BuildingCode.Boca:
    case BuildingCode.CanNbcc1995:
    case BuildingCode.CanNbcc2005:
    case BuildingCode.CanNbcc2010:
    case BuildingCode.CanNbcc2015:
    case BuildingCode.Fbc2001:
    case BuildingCode.Fbc2004:
    case BuildingCode.Fbc2007:
    case BuildingCode.Frc2004:
    case BuildingCode.Frc2007:
    case BuildingCode.Fresno:
    case BuildingCode.Hud:
    case BuildingCode.Hud175nd:
    case BuildingCode.Hud200nd:
    case BuildingCode.Hud25dest:
    case BuildingCode.Ibc2000:
    case BuildingCode.Ibc2003:
    case BuildingCode.Ibc2006:
    case BuildingCode.Ibc2009:
    case BuildingCode.Irc2000:
    case BuildingCode.Irc2003:
    case BuildingCode.Irc2006:
    case BuildingCode.Irc2009:
    case BuildingCode.Mnsrc:
    case BuildingCode.NbcBoca:
    case BuildingCode.Ohiobc03:
    case BuildingCode.Ohiobc07:
    case BuildingCode.Ohiobc11:
    case BuildingCode.Ohiobc13:
    case BuildingCode.Ohiorc03:
    case BuildingCode.Ohiorc07:
    case BuildingCode.Ohiorc11:
    case BuildingCode.Ohiorc13:
    case BuildingCode.Sbc:
    case BuildingCode.Sfbc:
    case BuildingCode.Ubc:
    case BuildingCode.Ubc97:
    case BuildingCode.Wisc:
    case BuildingCode.WiscIbc:
    case BuildingCode.WiscIbc2006:
    case BuildingCode.WiscIbc2009:
    case BuildingCode.WiscIrc:
    case BuildingCode.WiscIrc2006:
      return null;

    default:
      return assertExhaustiveSwitch(buildingCode);
  }
}
